import {
  Drawer,
  Segmented,
  useExpandedRowKeysContext,
  useTableRowSelection,
} from '@/components';
import { useUploadContractorPayment } from '@/features/project/_hooks/contractor-payment-uploader';
import type { PaymentType } from '@/features/project/projects/_models/payment';
import {
  useContractorPayments,
  useCreatePayment,
  useUpdatePayment,
} from '@/features/project/projects/detail/payment/_hooks/payment';
import { usePaymentGroups } from '@/features/project/projects/detail/payment/_hooks/payment-group';
import { useId } from '@/hooks';
import { useMessage } from '@/providers';
import Papa from 'papaparse';
import { type FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import invariant from 'tiny-invariant';
import { useProjectDetailDrawerState } from '../../_components/ProjectDrawerStateProvider';
import { usePreviewRowState } from '../../income-and-expenditure/_components/shared/PreviewRowStateProvider';
import { ContractorPaymentUploadForm } from './ContractorPaymentUploadForm';
import { PaymentForm } from './PaymentForm';

type Props = {
  type: PaymentType;
};

type SegmentType = 'single' | 'upload';

export const PaymentFormDrawer: FC<Props> = ({ type }) => {
  const { t } = useTranslation();
  const { drawerState, setDrawerState } = useProjectDetailDrawerState();
  const { setPreviewRowState } = usePreviewRowState();
  const { clearAllSelections } = useTableRowSelection();
  const { addExpandedRowKeys } = useExpandedRowKeysContext();
  const { openMessage } = useMessage();

  const [segment, setSegment] = useState<SegmentType>('single');

  const { id: projectId } = useId();
  const paymentGroups = usePaymentGroups({ projectId });
  const payments = useContractorPayments({ projectId });

  const { mutateAsync: createPaymentAsync } = useCreatePayment();
  const { mutateAsync: updatePaymentAsync } = useUpdatePayment();
  const { mutateAsync: uploadContractorPayment } = useUploadContractorPayment();

  const handleClose = async () => {
    setDrawerState({ open: false });
    clearAllSelections();
  };

  const initialValues = useMemo(() => {
    switch (type) {
      case 'payee':
        if (!drawerState.open || drawerState.type !== 'payee-payment') {
          return {};
        }
        return drawerState.isEditing
          ? paymentGroups.data
              ?.find((pg) => pg.id === drawerState.paymentGroupId)
              ?.payments.find((payment) => payment.id === drawerState.paymentId)
          : {
              type,
              paymentGroupId: drawerState.paymentGroupId,
            };
      case 'contractor':
        if (!drawerState.open || drawerState.type !== 'contractor-payment') {
          return {};
        }
        return drawerState.isEditing
          ? payments.data?.find(
              (payment) => payment.id === drawerState.paymentId,
            )
          : {
              type,
            };
      default:
        throw new Error('unreachable-code');
    }
  }, [type, drawerState, paymentGroups.data, payments.data]);

  const isContractorType = type === 'contractor';
  const openDrawer = drawerState.open && drawerState.type === `${type}-payment`;
  const isEditing =
    (type === 'payee' &&
      drawerState.open &&
      drawerState.type === 'payee-payment' &&
      drawerState.isEditing) ||
    (type === 'contractor' &&
      drawerState.open &&
      drawerState.type === 'contractor-payment' &&
      drawerState.isEditing);

  return (
    <Drawer
      open={openDrawer}
      onClose={handleClose}
      afterOpenChange={(visible) => {
        if (!visible) {
          setSegment('single');
        }
      }}
      destroyOnClose
    >
      {isContractorType && openDrawer && !isEditing && (
        <Segmented
          value={segment}
          onChange={(value) => {
            setSegment(value as SegmentType);
          }}
          options={[
            { label: t('create-single-item'), value: 'single' },
            { label: t('csv-upload'), value: 'upload' },
          ]}
          style={{ marginBottom: 16 }}
        />
      )}
      {segment === 'single' && (
        <PaymentForm
          initialValues={{
            ...initialValues,
            corporateNumber: initialValues
              ? 'corporateNumber' in initialValues
                ? initialValues.corporateNumber
                  ? Number(initialValues.corporateNumber)
                  : undefined
                : undefined
              : undefined,
          }}
          isEditing={isEditing}
          onSubmit={async ({ values, nextDrawerKey, onSuccess }) => {
            invariant(
              drawerState.open &&
                (drawerState.type === 'payee-payment' ||
                  drawerState.type === 'contractor-payment'),
              t('unreachable-code'),
            );

            if (drawerState.isEditing) {
              const res = await updatePaymentAsync({
                ...values,
                id: drawerState.paymentId,
                projectId,
                corporateNumber: values.corporateNumber
                  ? values.corporateNumber.toString()
                  : undefined,
              });

              await Promise.all([
                openMessage('success', t('message-update-completed')),
                (async () => {
                  addExpandedRowKeys([res.id]);
                })(),
                handleClose(),
              ]);
            } else {
              const res = await createPaymentAsync({
                ...values,
                corporateNumber: values.corporateNumber
                  ? values.corporateNumber.toString()
                  : undefined,
                projectId,
              });

              await Promise.all([
                openMessage('success', t('add-completed')),
                (async () => {
                  if (!nextDrawerKey) {
                    handleClose();
                  }

                  if (nextDrawerKey === 'payment-contract') {
                    setDrawerState({
                      open: true,
                      type: nextDrawerKey,
                      isEditing: false,
                      paymentGroupId: res.paymentGroupId,
                      paymentId: res.id,
                      paymentType: res.type,
                    });
                    setPreviewRowState({
                      type: nextDrawerKey,
                      paymentGroupId: res.paymentGroupId,
                      paymentId: res.id,
                      paymentType: res.type,
                    });
                  }

                  addExpandedRowKeys([res.id]);
                })(),
                (async () => {
                  onSuccess?.();
                })(),
              ]);
            }
          }}
        />
      )}
      {segment === 'upload' && isContractorType && (
        <ContractorPaymentUploadForm
          projectId={projectId}
          onSubmit={async (file: File) => {
            const reader = new FileReader();
            reader.readAsText(file);
            reader.onloadend = async () => {
              if (typeof reader.result !== 'string') {
                await openMessage(
                  'error',
                  t('error-message-invalid-reader-result'),
                );
                return;
              }

              const csvData = Papa.parse<Array<string | number>>(
                reader.result,
                {
                  skipEmptyLines: true,
                },
              );

              try {
                await uploadContractorPayment({
                  projectId,
                  csvData: csvData.data,
                });

                await Promise.all([
                  openMessage(
                    'success',
                    t('message-bulk-upload-process-requested'),
                  ),
                  handleClose(),
                ]);
              } catch {
                await openMessage('error', t('message-update-failed'));
              }
            };
          }}
        />
      )}
    </Drawer>
  );
};
